import { EnhancedModel } from '../__generated/configurator-builder.js'
import { implementCustomExpressions } from '../__generated/implementation.js'
import type { BaseDoubleLeg } from '../__generated/model/index.js'
import { SINGLE_BASE_ALTERNATIVE_MAP } from '../data/base-data.js'
import { FINISH_MAP, WOOD_SPECIES_TEXTURE_URLS, getWoodMaterialProps } from '../data/texture-data.js'
import { getBaseInset } from '../data/top-data.js'

export function interpolateByScale(
  base: number,
  max: number,
  value: number,
  scaleZero: number,
  scaleOne: number
): number {
  if (scaleOne === scaleZero) {
    return base
  }

  const t = (value - scaleZero) / (scaleOne - scaleZero)
  return base + t * (max - base)
}

export function getScaleShapekeyValue(
  widthShapekeyValue: number,
  widthScaleZero: number,
  widthScaleOne: number,
  scaleZero: number,
  scaleOne: number,
  increment: number,
  activeScaleIncrement: number
): number {
  if (widthScaleOne === widthScaleZero) {
    return scaleZero + activeScaleIncrement
  }

  const t = (widthShapekeyValue - widthScaleZero) / (widthScaleOne - widthScaleZero)
  const rawScale = scaleZero + t * (scaleOne - scaleZero)

  if (!increment || increment <= 0) {
    return Math.min(scaleOne, Math.max(scaleZero, rawScale)) + activeScaleIncrement
  }

  const steps = Math.round((rawScale - scaleZero) / increment)
  const stepped = scaleZero + steps * increment

  return Math.min(scaleOne, Math.max(scaleZero, stepped)) + activeScaleIncrement
}

export function getScaledSizingValue(
  sizing: Record<string, unknown>,
  valueKey: string,
  scaleOneKey: string,
  scaleShapekeyValue: number,
  scaleZero: number,
  scaleOne: number
): number {
  if (!(valueKey in sizing) || typeof sizing[valueKey] !== 'number') {
    return 0
  }

  const base = sizing[valueKey]
  const max = scaleOneKey in sizing && typeof sizing[scaleOneKey] === 'number' ? sizing[scaleOneKey] : base

  return interpolateByScale(base, max, scaleShapekeyValue, scaleZero, scaleOne)
}

export const BASE_THICKNESS_CENTER_Y_TYPES = new Set<BaseDoubleLeg['type']>([
  'CaseyBase',
  'BrewerBase',
  'DanaBase',
  'OswaldBase',
  'RishBase',
  'CraftsmanBase',
  'CrescentBase',
  'HershBase',
  'TamaraBase',
  'XBase',
  'AnglePictureFrameBase',
  'ArchBase',
  'BoxFrameBase',
  'ConnectedXBase',
  'SlimConnectedXBase',
  'XMetalBase',
])

export const baseDoubleLegExpressions = implementCustomExpressions({
  BaseDoubleLeg: {
    $width: s => {
      if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
        return s.$scaleLowerBreakpoint * s.$baseWidthMultiplier + s.$activeScaleIncrement
      }
      return s.$table.$top.$width * s.$baseWidthMultiplier
    },
    // Math.max(
    //   s.$table.$top.$width * s.$baseWidthMultiplier,
    //   s.$topWidthLowerLimitForBaseWidth * s.$baseWidthMultiplier
    // ),
    $widthShapekeyValue: s => {
      if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
        return s.$scaleLowerBreakpoint * s.$baseWidthMultiplier
      }
      return s.$table.$top.$width * s.$baseWidthMultiplier - s.$activeScaleIncrement
    },
    $widthScaleZero: s => s.$baseDataMap[s.type]?.sizing.width.scaleZero,
    $widthScaleOne: s => s.$baseDataMap[s.type]?.sizing.width.scaleOne,

    $activeScaleIncrement: s => {
      if (s.$table.$top.$width > s.$scaleUpperBreakpoint) {
        return s.$breakpointScaleIncrement
      }
      if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
        return -s.$breakpointScaleIncrement
      }
      return 0
    },

    $scaleShapekeyValue: s => s.$scaleScaleZero + s.$activeScaleIncrement,
    $scaleScaleZero: s => s.$baseDataMap[s.type].sizing.scale.scaleZero,
    $scaleScaleOne: s => s.$baseDataMap[s.type].sizing.scale.scaleOne,

    $centerScaleShapekeyValue: s => s.$centerScaleScaleZero + s.$activeScaleIncrement,
    $centerScaleScaleZero: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        return s.$baseDataMap.AnglePictureFrameBase.sizing.scale.scaleZero
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        return s.$baseDataMap.OswaldBase.sizing.scale.scaleZero
      }
      return s.$scaleScaleZero
    },
    $centerScaleScaleOne: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        return s.$baseDataMap.AnglePictureFrameBase.sizing.scale.scaleOne
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        return s.$baseDataMap.OswaldBase.sizing.scale.scaleOne
      }
      return s.$scaleScaleOne
    },

    $baseWidthMultiplier: s => s.$baseDataMap[s.type].sizing.width.scaleZero / s.$table.$top.$defaultWidth,

    $offsetZLeft: s => {
      let topLength = s.$table.$top.$length
      if (s.$table.$top.$leafCenterVisible) {
        topLength += s.$table.$top.$leafLength * s.$table.$top.leafAmount
      }
      const distanceToTableEdge = topLength / 2
      const legThickness = EnhancedModel.$RogersBase.is(s) ? s.$baseDataMap[s.type]?.sizing.width.scaleZero : 0
      const ovalExtraInset = EnhancedModel.$TopOval.is(s.$table.$top) ? 6 : 0
      const inset = getBaseInset(s.$table.$top.$length) + legThickness / 4 + ovalExtraInset
      const offsetZ = distanceToTableEdge - inset

      return offsetZ * -1
    },
    $offsetZRight: s => {
      let topLength = s.$table.$top.$length
      if (s.$table.$top.$leafCenterVisible) {
        topLength += s.$table.$top.$leafLength * s.$table.$top.leafAmount
      }
      const distanceToTableEdge = topLength / 2
      const legThickness = EnhancedModel.$RogersBase.is(s) ? s.$baseDataMap[s.type]?.sizing.width.scaleZero : 0
      const ovalExtraInset = EnhancedModel.$TopOval.is(s.$table.$top) ? 6 : 0
      const inset = getBaseInset(s.$table.$top.$length) + legThickness / 4 + ovalExtraInset
      const offsetZ = distanceToTableEdge - inset

      return offsetZ
    },
    $visibleCenter: s => s.$table.$top.$length > s.$table.$top.$minTopLengthForExtraBase,
    $singleLegAlternative: s => SINGLE_BASE_ALTERNATIVE_MAP[s.$table.$base.type as BaseDoubleLeg['type']],

    $centerObjectName: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        return s.$baseDataMap.AnglePictureFrameBase.objectName
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        return s.$baseDataMap.OswaldBase.objectName
      }
      return s.$objectName
    },
    $centerWidth: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        const apfScaleZero = s.$baseDataMap.AnglePictureFrameBase.sizing.width.scaleZero
        const multiplier = apfScaleZero / s.$table.$top.$defaultWidth
        // return Math.max(s.$table.$top.$width * multiplier, s.$topWidthLowerLimitForBaseWidth * multiplier)
        return s.$table.$top.$width * multiplier
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        const oswaldScaleZero = s.$baseDataMap.OswaldBase.sizing.width.scaleZero
        const multiplier = oswaldScaleZero / s.$table.$top.$defaultWidth
        return s.$table.$top.$width * multiplier
      }
      return s.$table.$top.$width * s.$baseWidthMultiplier
    },
    $centerWidthShapekeyValue: s => {
      if (s.$table.$top.$width > s.$scaleUpperBreakpoint) {
        return s.$scaleUpperBreakpoint * s.$baseWidthMultiplier
      }
      if (s.$table.$top.$width < s.$scaleLowerBreakpoint) {
        return s.$scaleLowerBreakpoint * s.$baseWidthMultiplier
      }
      return s.$centerWidth
    },
    $centerWidthScaleZero: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        return s.$baseDataMap.AnglePictureFrameBase.sizing.width.scaleZero
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        return s.$baseDataMap.OswaldBase.sizing.width.scaleZero
      }
      return s.$widthScaleZero
    },
    $centerWidthScaleOne: s => {
      if (EnhancedModel.$TriflectionBase.is(s)) {
        return s.$baseDataMap.AnglePictureFrameBase.sizing.width.scaleOne
      }
      if (EnhancedModel.$CaseyBase.is(s)) {
        return s.$baseDataMap.OswaldBase.sizing.width.scaleOne
      }
      return s.$widthScaleOne
    },

    $needTwoCrossbraces: s => s.hasCrossbrace && s.$visibleCenter,

    $crossbraceType: s => s.$baseDataMap[s.type].crossbraceType ?? 'StandardCrossbrace',
    $crossbraceObjectName: s => s.$crossbraceDataMap[s.$crossbraceType].objectName,

    $crossbraceWidthScaleZero: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.width.scaleZero,
    $crossbraceWidthScaleOne: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.width.scaleOne,
    $crossbraceLengthScaleZero: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.length.scaleZero,
    $crossbraceLengthScaleOne: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.length.scaleOne,
    $crossbraceHeightScaleZero: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.height.scaleZero,
    $crossbraceHeightScaleOne: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.height.scaleOne,
    $crossbraceScaleScaleZero: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.scale.scaleZero,
    $crossbraceScaleScaleOne: s => s.$crossbraceDataMap[s.$crossbraceType].sizing.scale.scaleOne,

    $crossbraceOffsetY: s => {
      const crossbraceData = s.$baseDataMap[s.type].crossbraceData
      return crossbraceData ? crossbraceData.offsetY : 0
    },
    $crossbraceWidthShapekeyValue: s => {
      const crossbraceData = s.$baseDataMap[s.type].crossbraceData
      return crossbraceData ? crossbraceData.width : 0
    },
    $crossbraceOffsetZ: s => (s.$needTwoCrossbraces ? s.$offsetZLeft! / 2 : 0),
    $crossbraceSecondaryOffsetZ: s => (s.$needTwoCrossbraces ? s.$offsetZRight! / 2 : 0),

    $crossbraceLengthShapekeyValue: s =>
      Math.abs(s.$offsetZLeft!) +
      (!s.$needTwoCrossbraces ? Math.abs(s.$offsetZRight!) : 0) +
      (s.$baseDataMap[s.type].crossbraceData?.lengthExtension ?? 0) * 2,
    $crossbraceHeightShapekeyValue: s => {
      const crossbraceData = s.$baseDataMap[s.type].crossbraceData
      return crossbraceData ? crossbraceData.height : 0
    },
    $crossbraceScaleShapekeyValue: s => s.$crossbraceScaleScaleZero,

    $crossbraceRepeatX: s => s.$crossbraceHeightShapekeyValue / s.$table.$woodTextureSize,
    $crossbraceRepeatY: s => s.$crossbraceLengthShapekeyValue / s.$table.$woodTextureSize,

    $crossbraceFinishColor: s => FINISH_MAP[s.finish]?.colorHex ?? '#FFFFFF',
    $crossbraceTextureUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.baseColor,
    $crossbraceNormalMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.normal,
    $crossbraceRoughnessMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.roughness,
    $crossbraceAoMapUrl: s => WOOD_SPECIES_TEXTURE_URLS[s.$table.woodSpecies]?.ambientOcclusion,
    $crossbraceRoughness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).roughness,
    $crossbraceMetalness: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).metalness,
    $crossbraceEnvMapIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).envMapIntensity,
    $crossbraceSpecularIntensity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).specularIntensity,
    $crossbraceNormalScale: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).normalScale,
    $crossbraceReflectivity: s => getWoodMaterialProps(s.$table.woodSpecies, s.$table.sheen).reflectivity,

    $widthIndicatorFixedY: () => 0,
    $widthIndicatorFixedZ: s => s.$offsetZRight!,
    $widthIndicatorExtrusionZ: () => 12,

    $feetThickness: s => {
      const sizing = s.$baseDataMap[s.type].sizing
      return getScaledSizingValue(
        sizing,
        'feetThickness',
        'feetThicknessScaleOne',
        s.$scaleShapekeyValue,
        s.$scaleScaleZero,
        s.$scaleScaleOne
      )
    },
    $feetThicknessIndicatorFixedX: s => s.$width / 2,
    $feetThicknessIndicatorStartZ: s => s.$offsetZRight! - s.$feetThickness / 2,
    $feetThicknessIndicatorEndZ: s => s.$offsetZRight! + s.$feetThickness / 2,

    $baseThickness: s => {
      const sizing = s.$baseDataMap[s.type].sizing
      return getScaledSizingValue(
        sizing,
        'baseThickness',
        'baseThicknessScaleOne',
        s.$scaleShapekeyValue,
        s.$scaleScaleZero,
        s.$scaleScaleOne
      )
    },
    $baseThicknessIndicatorFixedY: s => {
      if (BASE_THICKNESS_CENTER_Y_TYPES.has(s.type)) {
        return s.$defaultHeight / 2
      }

      const sizing = s.$baseDataMap[s.type].sizing
      return getScaledSizingValue(
        sizing,
        'baseThicknessCenterY',
        'baseThicknessCenterYScaleOne',
        s.$scaleShapekeyValue,
        s.$scaleScaleZero,
        s.$scaleScaleOne
      )
    },
    $baseThicknessIndicatorVisible: s => !EnhancedModel.$TriflectionBase.is(s),
    $baseThicknessIndicatorStartZ: s => s.$offsetZRight! - s.$baseThickness / 2,
    $baseThicknessIndicatorEndZ: s => s.$offsetZRight! + s.$baseThickness / 2,
    $baseThicknessIndicatorExtrusionX: s => s.$width / 2 + 12,
  },
})
